<template>
  <!-- Solo la ve un responsable autorizado de Microsoft 365: a quien no lo es, la tarjeta no se
       dibuja (regla §9: un punto de entrada que lleva a un 403 no se pinta). Lo decide la API en
       `/microsoft365/estado`, no un rol escrito aquí. -->
  <f7-card class="padding-vertical-half padding-right tarjeta-m365" v-if="estado && estado.puedeGestionar">
    <f7-card-header>
      <span>Microsoft 365</span>
      <span class="m365-modo" :class="'modo-' + estado.modo">{{ rotuloModo }}</span>
    </f7-card-header>
    <f7-card-content>
      <div class="m365">
        <p class="nota" v-if="estado.modo === 'SIMULADO'">
          Simulación: trabaja contra un tenant ficticio y no toca Microsoft. Solo existen las cuentas ficticias
          (correo que empieza por «ficticio.»).
        </p>
        <p class="aviso" v-if="estado.modo === 'DESACTIVADO'">
          {{ estado.motivoModo }} La activa el administrador del sistema en la configuración del servidor.
        </p>

        <!-- En el alta todavía no hay empleado al que colgarle nada: se dice y se para aquí. -->
        <p class="nota" v-if="!empleadoId">
          Guarda primero la ficha del empleado. Después podrás preparar y solicitar aquí su alta en Microsoft 365.
        </p>

        <template v-else>
          <div class="estado-carga" v-if="cargando && !ficha"><f7-preloader></f7-preloader><span>Cargando Microsoft 365…</span></div>
          <div class="fallo" v-else-if="errorCarga">
            <div>{{ errorCarga }}</div>
            <button class="m365btn" @click="cargar">Volver a cargar</button>
          </div>

          <template v-else-if="ficha">
            <!-- Datos de la tarjeta. Son de Microsoft 365, no de la ficha: no cambian el Rol, ni
                 «Crear Usuario de aplicación», ni ningún permiso del ERP. -->
            <div class="bloque">
              <div class="titulo">Datos para Microsoft 365</div>
              <p class="nota" v-if="!ficha.perfil">
                Propuestos a partir de la ficha (rol, correo y nombre). Revísalos y guárdalos antes de solicitar nada.
              </p>
              <div class="rejilla">
                <label>Perfil Microsoft 365
                  <select v-model="form.perfil" :disabled="!puedeEditar">
                    <option :value="null" disabled>Elige un perfil</option>
                    <option v-for="p in PERFILES" :key="p.id" :value="p.id">{{ p.nombre }}</option>
                  </select>
                </label>
                <label>Correo de acceso
                  <input type="email" v-model.trim="form.correo" :disabled="!puedeCambiarCorreo" />
                </label>
                <label>Nombre
                  <input type="text" v-model.trim="form.nombre" :disabled="!puedeCambiarDatosCuenta" />
                </label>
                <label>Apellidos
                  <input type="text" v-model.trim="form.apellidos" :disabled="!puedeCambiarDatosCuenta" />
                </label>
                <label>Ubicación de uso
                  <input type="text" maxlength="2" v-model.trim="form.ubicacionUso" :disabled="!puedeCambiarDatosCuenta" />
                </label>
                <label>Fecha de incorporación (de la ficha)
                  <input type="text" :value="fecha(ficha.fechaAlta) || 'Sin fecha'" disabled />
                </label>
              </div>
              <!-- Junto a los campos que bloquean: son datos que ya tiene Microsoft. Si el motivo del
                   correo es el mismo que el del resto, se dice una vez. -->
              <p class="motivo" v-if="puedeEditar && !puedeCambiarCorreo && motivoCorreo !== accion('cambiarDatosCuenta').motivo">
                {{ motivoCorreo }}
              </p>
              <p class="motivo" v-if="puedeEditar && !puedeCambiarDatosCuenta">{{ accion('cambiarDatosCuenta').motivo }}</p>
              <p class="nota">
                El perfil decide a qué grupos de Microsoft se añade a la persona. No cambia su rol ni sus permisos en el ERP.
                Si se cambia cuando ya tiene grupos, «Reintentar» la añade a los del perfil nuevo; de los del anterior no se la saca.
                Rol en el ERP: <b>{{ ficha.rol || 'sin rol' }}</b>.
              </p>
              <div class="accion">
                <button class="m365btn" :disabled="!accion('guardarDatos').disponible || guardando || ocupado" @click="guardar">
                  Guardar datos de Microsoft 365
                </button>
                <span class="motivo" v-if="!accion('guardarDatos').disponible">{{ accion('guardarDatos').motivo }}</span>
              </div>
            </div>

            <!-- Lo que impide hoy dar accesos, junto a los botones que lo bloquea: no arriba suelto. -->
            <ul class="avisos" v-if="ficha.motivosNoElegible && ficha.motivosNoElegible.length">
              <li v-for="(m, i) in ficha.motivosNoElegible" :key="'ne-' + i">{{ m }}</li>
            </ul>

            <div class="bloque" v-if="!ficha.vinculo">
              <div class="titulo">Cuenta</div>
              <div class="acciones">
                <div class="accion">
                  <button class="m365btn" :disabled="!accion('solicitarAlta').disponible || ocupado" @click="solicitarAlta">
                    Solicitar alta en Microsoft 365
                  </button>
                  <span class="motivo" v-if="!accion('solicitarAlta').disponible">{{ accion('solicitarAlta').motivo }}</span>
                </div>
                <div class="accion">
                  <button class="m365btn sec" :disabled="!accion('vincular').disponible || ocupado" @click="comprobar">
                    Comprobar cuenta existente
                  </button>
                  <span class="motivo" v-if="!accion('vincular').disponible">{{ accion('vincular').motivo }}</span>
                </div>
              </div>
              <!-- Lo que hay en Microsoft para este correo, sin haber cambiado nada. Solo una cuenta
                   inequívoca se puede vincular; los reparos se enseñan tal cual. -->
              <div class="correspondencia" v-if="correspondencia">
                <div v-if="correspondencia.estado === 'NO_ENCONTRADA'">
                  No hay ninguna cuenta de Microsoft con <b>{{ correspondencia.correo }}</b>. Si la persona necesita una,
                  usa «Solicitar alta».
                </div>
                <div v-else>
                  <div v-if="correspondencia.upn">
                    Cuenta encontrada: <b>{{ correspondencia.nombre }}</b> ({{ correspondencia.upn }})
                    · {{ correspondencia.tipo === 'Guest' ? 'invitada' : 'miembro' }}
                    · Business Premium: {{ rotuloLicencia(correspondencia.licencia) }}
                  </div>
                  <ul class="avisos" v-if="correspondencia.conflictos.length">
                    <li v-for="(c, i) in correspondencia.conflictos" :key="'cf-' + i">{{ c }}</li>
                  </ul>
                  <div class="accion" v-if="correspondencia.vinculable">
                    <button class="m365btn" :disabled="ocupado" @click="vincular">Vincular esta cuenta</button>
                    <span class="nota">Se vincula sin recrearla, sin cambiar su contraseña ni su licencia.</span>
                  </div>
                  <div class="motivo" v-else>
                    No se puede vincular mientras haya reparos: se revisan en el Centro de administración de Microsoft 365
                    o se corrige el correo de la ficha.
                  </div>
                </div>
              </div>
            </div>
            <div class="bloque" v-else>
              <div class="titulo">Cuenta</div>
              <div>
                Vinculada a <b>{{ ficha.vinculo.upn }}</b>
                ({{ ficha.vinculo.origen === 'CREADA' ? 'creada por este proceso' : 'cuenta que ya existía' }})
                el {{ fechaHora(ficha.vinculo.vinculadoEn) }} por {{ ficha.vinculo.vinculadoPor }}.
              </div>
            </div>

            <!-- El panel de la solicitud: un estado por paso, con su motivo y qué hacer. -->
            <div class="bloque" v-if="ficha.solicitud">
              <div class="titulo">
                {{ ficha.solicitud.tipo === 'ALTA' ? 'Alta nueva' : 'Vinculación de cuenta existente' }}
                <span class="chip" :class="'e-' + ficha.solicitud.estado">{{ rotuloSolicitud(ficha.solicitud.estado) }}</span>
              </div>
              <div class="nota">
                Pedida por {{ ficha.solicitud.solicitadaPor }} el {{ fechaHora(ficha.solicitud.solicitadaEn) }}
                <template v-if="ficha.ultimaComprobacion"> · última comprobación {{ fechaHora(ficha.ultimaComprobacion) }}</template>
                <template v-if="vivaSolicitud"> · se actualiza sola mientras está en curso</template>
                <template v-if="leidaEn"> · leída a las {{ hora(leidaEn) }}</template>
              </div>
              <!-- Junto a la hora de lectura: si el sondeo no llega, lo que hay a la vista es lo último leído, no lo de ahora. -->
              <div class="fallo" v-if="errorSondeo">{{ errorSondeo }}</div>
              <table class="pasos">
                <thead>
                  <tr><th scope="col">Paso</th><th scope="col">Estado</th><th scope="col">Detalle y qué hacer</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in ficha.pasos" :key="p.paso">
                    <td class="nombre">{{ NOMBRES_PASO[p.paso] || p.paso }}</td>
                    <td><span class="chip" :class="'e-' + p.estado">{{ rotuloPaso(p.estado) }}</span></td>
                    <td>
                      <div v-if="p.mensaje">{{ p.mensaje }}</div>
                      <div class="que-hacer" v-if="p.accion && !resuelto(p.estado)">{{ p.accion }}</div>
                      <ul class="pertenencias" v-if="p.pertenencias && p.pertenencias.length">
                        <li v-for="(g, i) in p.pertenencias" :key="p.paso + '-' + i">
                          <span class="chip" :class="'e-' + g.estado">{{ rotuloPaso(g.estado) }}</span>
                          <b>{{ g.correo || g.nombre }}</b> <span class="nota">({{ ROTULO_DESTINO[g.destino] || g.destino }})</span>
                          <span v-if="g.mensaje"> · {{ g.mensaje }}</span>
                        </li>
                      </ul>
                      <!-- Archivo y conservación: cada comprobación con lo que se leyó del buzón, para
                           que se vea cuál falta y no un único estado que las tape. -->
                      <ul class="pertenencias" v-if="p.comprobaciones && p.comprobaciones.length">
                        <li v-for="(c, i) in p.comprobaciones" :key="p.paso + '-c-' + i">
                          <span class="chip" :class="'e-' + c.estado">{{ rotuloPaso(c.estado) }}</span>
                          <b>{{ c.nombre }}</b>
                          <span v-if="c.mensaje"> · {{ c.mensaje }}</span>
                        </li>
                      </ul>
                      <div class="nota" v-if="p.verificadoEn">Leído del buzón el {{ fechaHora(p.verificadoEn) }}.</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="acciones">
                <div class="accion">
                  <button class="m365btn" :disabled="!accion('reintentar').disponible || ocupado" @click="reintentar">Reintentar</button>
                  <span class="motivo" v-if="!accion('reintentar').disponible">{{ accion('reintentar').motivo }}</span>
                </div>
                <!-- El archivo y la conservación se aplican y se comprueban en el buzón. La declaración a
                     mano solo se ofrece mientras el servidor no tiene con qué comprobarlo (falta el
                     conector de Exchange y Purview), y deja el alta «sin verificar», nunca completada. -->
                <div class="accion" v-if="ficha.solicitud.tipo === 'ALTA' && accion('confirmarArchivo').disponible">
                  <button class="m365btn sec" :disabled="ocupado" @click="confirmarArchivo">
                    Declarar archivo y conservación aplicados a mano
                  </button>
                  <span class="nota">No es una verificación técnica de Exchange ni de Purview: el alta seguirá «sin verificar».</span>
                </div>
                <div class="accion" v-if="ficha.vinculo && ficha.vinculo.origen === 'CREADA'">
                  <button class="m365btn sec" :disabled="!accion('accesoTemporal').disponible || ocupado" @click="accesoTemporal">
                    Emitir acceso temporal
                  </button>
                  <span class="motivo" v-if="!accion('accesoTemporal').disponible">{{ accion('accesoTemporal').motivo }}</span>
                </div>
              </div>
            </div>

            <!-- Cuenta y equipo son dos cosas: que la cuenta esté lista no inscribe ni configura ningún PC. -->
            <div class="bloque dos">
              <div>
                <div class="titulo">Cuenta preparada</div>
                <div>{{ ficha.cuentaPreparada ? 'Sí: cuenta, licencia, grupos y buzón comprobados en Microsoft.' : 'Todavía no.' }}</div>
                <div class="nota" v-if="pasoArchivo">
                  Archivo y conservación: {{ rotuloPaso(pasoArchivo.estado) }}.
                  <template v-if="!resuelto(pasoArchivo.estado)">El alta no está terminada hasta que se comprueben en el buzón.</template>
                </div>
              </div>
              <div>
                <div class="titulo">Equipo inscrito y configurado</div>
                <div>
                  Este proceso no inscribe equipos en Intune ni les aplica perfiles: añadir la cuenta a un grupo no prepara el PC.
                  Un Windows 11 que ya se usa se inscribe sin formatearlo, entrando con esta cuenta en «Configuración › Cuentas ›
                  Obtener acceso a trabajo o escuela › Conectar › Unir este dispositivo a Microsoft Entra ID»; después un
                  administrador comprueba en Intune que ha recibido sus directivas y aplicaciones (guía de Microsoft 365, «Equipos»).
                </div>
                <div class="accion" v-if="ficha.vinculo">
                  <button class="m365btn sec" :disabled="ocupado" @click="consultarEquipos">Consultar equipos inscritos</button>
                  <span class="nota" v-if="equipos">{{ equipos }}</span>
                </div>
              </div>
            </div>

            <div class="fallo" v-if="errorSondeo && !ficha.solicitud">{{ errorSondeo }}</div>
            <div class="fallo" v-if="errorAccion">{{ errorAccion }}</div>
          </template>
        </template>
      </div>
    </f7-card-content>
  </f7-card>
</template>

<script>
import Microsoft365 from './../../js/db/Microsoft365.js';
import cerrojoDeLaSesion from './../../js/utils/cerrojoDeLaSesion.js';

const PERFILES = [
  { id: 'INSPECCION', nombre: 'Inspección' },
  { id: 'COMERCIAL', nombre: 'Comercial' },
  { id: 'ADMINISTRACION', nombre: 'Administración' }
];
const NOMBRES_PASO = {
  VALIDACION: 'Validación',
  CUENTA: 'Cuenta',
  LICENCIA: 'Licencia Business Premium',
  GRUPOS: 'Grupos',
  BUZON: 'Buzón',
  ARCHIVO: 'Archivo y conservación'
};
const ROTULO_PASO = {
  PENDIENTE: 'Pendiente',
  EN_CURSO: 'En curso',
  COMPLETADO: 'Completado',
  YA_CONFIGURADO: 'Ya configurado',
  REQUIERE_INTERVENCION: 'Requiere intervención',
  ERROR: 'Error',
  NO_APLICA: 'No aplica',
  // Declarado por una persona y no comprobado por el proceso: nunca se pinta como completado.
  SIN_VERIFICAR: 'Declarado a mano, sin verificar'
};
const ROTULO_SOLICITUD = {
  PENDIENTE: 'Pendiente',
  EN_CURSO: 'En curso',
  REQUIERE_INTERVENCION: 'Requiere intervención',
  ERROR: 'Error',
  SIN_VERIFICAR: 'Sin verificar: archivo y conservación',
  COMPLETADA: 'Completada'
};
const ROTULO_DESTINO = { PERFIL: 'grupo del perfil', SEGURIDAD: 'grupo de seguridad', EQUIPO: 'SIMECAL EQUIPO' };
const ROTULO_LICENCIA = { ACTIVA: 'activa', PENDIENTE: 'aplicándose', ERROR: 'en error', SIN_LICENCIA: 'no tiene' };
const RESUELTOS = ['COMPLETADO', 'YA_CONFIGURADO', 'NO_APLICA'];
// Campo del formulario, dato guardado en Microsoft 365 y sugerencia que sale de la ficha del empleado.
const SUGERENCIAS = [
  ['perfil', 'perfilSugerido', null],
  ['correo', 'correoSugerido', ''],
  ['nombre', 'nombreSugerido', ''],
  ['apellidos', 'apellidosSugeridos', ''],
  ['ubicacionUso', 'ubicacionPorDefecto', 'ES']
];
const RECARGA_MS = 8000;
// Ninguna petición de la tarjeta lleva tope propio: una lectura o una acción que no contestara la
// dejaba parada, con lo de antes a la vista y los botones apagados para siempre (prueba del 30/10/2026,
// con el servidor sin contestar a tiempo). Pasado el tope se da por perdida y la tarjeta sigue. Cada
// petición lleva el mismo tope, para que el navegador la cancele cuando la tarjeta ya no la espera.
const LECTURA_MAX_MS = 30000;
const ACCION_MAX_MS = 45000;
const SIN_RESPUESTA = 'El servidor no ha contestado a tiempo.';

// Con `replace` y una expresión global, no con `replaceAll`: el front se sigue construyendo para
// Safari 11 (browserslist) y `replaceAll` no existe ahí ni se transpila.
function escapar(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * Tarjeta «Microsoft 365» de la ficha del empleado (#1044). Vive fuera del r-form del empleado y
 * guarda por su cuenta, igual que las demás tarjetas: guardar la ficha no pide nada a Microsoft ni
 * reaprovisiona a nadie, y con la integración apagada la ficha funciona exactamente como antes.
 *
 * No sigue el «Ver/Editar» de la ficha a propósito, igual que la de Vacaciones: tiene su propio
 * permiso —el de responsable de Microsoft 365, que decide la API— y su propio guardado, y en el
 * alta la ficha se queda en modo lectura justo después de guardarla, que es cuando toca pedir el
 * alta en Microsoft. Por ese permiso propio se monta también en la ficha de lectura de DIRECTOR,
 * que no tiene el módulo de empleados: la tarjeta solo enseña lo de Microsoft 365, no el resto de
 * datos personales de la ficha.
 */
export default {
  props: {
    empleadoId: { type: String, default: null },
    username: { type: String, default: null }
  },
  data: function() {
    return {
      PERFILES: PERFILES,
      NOMBRES_PASO: NOMBRES_PASO,
      ROTULO_DESTINO: ROTULO_DESTINO,
      estado: null,
      ficha: null,
      form: { perfil: null, correo: '', nombre: '', apellidos: '', ubicacionUso: 'ES' },
      cargando: false,
      guardando: false,
      ocupado: false,
      errorCarga: null,
      // Una relectura que ha fallado con la tarjeta ya pintada: se avisa sin tapar lo último leído.
      errorSondeo: null,
      leidaEn: null,
      errorAccion: null,
      correspondencia: null,
      equipos: null,
      turno: 0,
      // Las consultas sueltas («Comprobar cuenta existente», equipos) no pasan por `turno`: su respuesta
      // vale solo si al llegar sigue siendo la misma persona y nadie ha descartado lo consultado. Ver `vigente`.
      consulta: 0,
      // Mientras una acción que devuelve la ficha está en vuelo, y la lectura que se haya pedido
      // entretanto (null si ninguna; si no, si refresca sugerencias). Ver `cargar`.
      enAccion: false,
      lecturaPendiente: null,
      temporizador: null,
      // Mientras la tarjeta exista. Una respuesta que llega después de destruirla no vuelve a
      // programar la recarga: si no, seguiría pidiendo la ficha cada pocos segundos sin nadie mirando.
      activa: true
    };
  },
  computed: {
    rotuloModo: function() {
      return { SIMULADO: 'Simulación', REAL: 'Conectado', DESACTIVADO: 'Desactivado' }[this.estado.modo] || this.estado.modo;
    },
    pasoArchivo: function() {
      const pasos = (this.ficha && this.ficha.pasos) || [];
      return this.ficha && this.ficha.solicitud && this.ficha.solicitud.tipo === 'ALTA' ? pasos.find(p => p.paso === 'ARCHIVO') : null;
    },
    vivaSolicitud: function() {
      const s = this.ficha && this.ficha.solicitud;
      return !!s && (s.estado === 'PENDIENTE' || s.estado === 'EN_CURSO');
    },
    puedeEditar: function() {
      return this.accion('guardarDatos').disponible;
    },
    puedeCambiarCorreo: function() {
      // Una API anterior no manda esta acción: entonces solo lo fija el vínculo, como antes.
      const a = this.ficha && this.ficha.acciones && this.ficha.acciones.cambiarCorreo;
      return this.puedeEditar && (a ? a.disponible : !this.ficha.vinculo);
    },
    motivoCorreo: function() {
      const a = this.ficha && this.ficha.acciones && this.ficha.acciones.cambiarCorreo;
      return a ? a.motivo : 'La ficha ya está vinculada: el correo de acceso no se cambia desde aquí.';
    },
    puedeCambiarDatosCuenta: function() {
      // Una API anterior no manda esta acción: entonces vale lo de guardar, y es ella la que decide.
      const a = this.ficha && this.ficha.acciones && this.ficha.acciones.cambiarDatosCuenta;
      return this.puedeEditar && (!a || a.disponible);
    }
  },
  watch: {
    empleadoId: function() {
      this.ficha = null;
      this.errorSondeo = null;
      this.leidaEn = null;
      this.olvidarConsultas();
      this.equipos = null;
      // Otra persona: lo que esté en vuelo era de la anterior y no debe pintarse aquí. Esta lectura
      // no se aplaza; al salir, invalida la respuesta de esa acción.
      this.enAccion = false;
      this.lecturaPendiente = null;
      this.cargar();
    }
  },
  created: function() {
    Microsoft365.estado().then(r => {
      if (!this.activa) return;
      this.estado = r;
      this.cargar();
    }).catch(() => {
      // Sin estado no se sabe si quien mira es responsable: no se pinta nada, que es lo seguro.
      this.estado = null;
    });
  },
  beforeDestroy: function() {
    this.activa = false;
    // Invalida las respuestas que estén en camino: ninguna pinta ni programa nada ya.
    this.turno++;
    this.consulta++;
    this.pararRecarga();
  },
  methods: {
    accion: function(nombre) {
      const a = this.ficha && this.ficha.acciones && this.ficha.acciones[nombre];
      return a || { disponible: false, motivo: 'Cargando…' };
    },
    resuelto: function(estado) {
      return RESUELTOS.includes(estado);
    },
    rotuloPaso: function(e) { return ROTULO_PASO[e] || e; },
    rotuloSolicitud: function(e) { return ROTULO_SOLICITUD[e] || e; },
    rotuloLicencia: function(l) { return ROTULO_LICENCIA[l] || 'no comprobada'; },
    fecha: function(v) {
      return v ? new Date(v).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' }) : '';
    },
    hora: function(v) {
      return v ? new Date(v).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid' }) : '';
    },
    fechaHora: function(v) {
      return v ? new Date(v).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', dateStyle: 'short', timeStyle: 'short' }) : '';
    },
    /** Descarta la cuenta encontrada y deja sin efecto las consultas que sigan en camino. */
    olvidarConsultas: function() {
      this.correspondencia = null;
      this.consulta++;
    },
    /**
     * Sello de una consulta suelta: la persona y, si lo que se consulta depende del correo guardado, la
     * vuelta en que salió. La respuesta solo se pinta si al llegar `vigente` lo reconoce; si no, era de
     * otra persona o de un correo que ya se ha cambiado. Los equipos no dependen del correo: guardar la
     * ficha no los descarta.
     */
    sellar: function(dependeDelCorreo) {
      return { empleadoId: this.empleadoId, consulta: dependeDelCorreo ? this.consulta : null };
    },
    vigente: function(sello) {
      return this.activa && sello.empleadoId === this.empleadoId && (sello.consulta === null || sello.consulta === this.consulta);
    },
    pararRecarga: function() {
      if (this.temporizador) {
        clearTimeout(this.temporizador);
        this.temporizador = null;
      }
    },
    /**
     * Relee la tarjeta después de guardar la ficha del empleado con el mismo id, que no dispara el
     * watcher: rol, fecha de incorporación, si está activo y las acciones disponibles salen de esa
     * ficha, y sin solicitud viva no hay sondeo que los ponga al día. La cuenta encontrada con
     * «Comprobar cuenta existente» se descarta: se buscó con el correo de antes de guardar.
     */
    recargar: function() {
      this.olvidarConsultas();
      this.cargar(true);
    },
    /**
     * La única lectura de la tarjeta: la usan la carga inicial, el sondeo, `recargar` al guardar la
     * ficha y la relectura tras un error. Con una acción en vuelo no sale: podría volver con la ficha
     * de antes de la acción y, por ser posterior, dejar sin pintar la respuesta de la acción, que es
     * la buena. Se apunta y la lanza `trabajar` cuando la acción termina.
     */
    cargar: function(refrescarSugerencias) {
      this.pararRecarga();
      if (this.enAccion) {
        this.lecturaPendiente = this.lecturaPendiente === true || refrescarSugerencias === true;
        return;
      }
      if (!this.activa || !this.estado || !this.estado.puedeGestionar || !this.empleadoId) return;
      const pedido = this.empleadoId;
      const turno = ++this.turno;
      cerrojoDeLaSesion(this, 'cargando');
      // Pasado el tope, la respuesta que llegue tarde ya no es la última pedida y no se pinta.
      const vigia = setTimeout(() => {
        if (turno !== this.turno || !this.activa) return;
        this.turno++;
        this.fallarLectura(SIN_RESPUESTA);
      }, LECTURA_MAX_MS);
      Microsoft365.ficha(pedido, LECTURA_MAX_MS).then(r => {
        if (turno !== this.turno || pedido !== this.empleadoId) return;
        this.errorCarga = null;
        this.errorSondeo = null;
        this.leidaEn = new Date();
        const anterior = this.ficha;
        this.pintar(r, !anterior);
        if (refrescarSugerencias === true && anterior) this.refrescarSugerencias(anterior, r);
      }).catch(e => {
        if (turno !== this.turno) return;
        this.fallarLectura((e && e.message) || 'No se ha podido cargar Microsoft 365 de esta persona.');
      }).then(() => {
        clearTimeout(vigia);
        if (turno === this.turno) this.cargando = false;
      });
    },
    /**
     * Sin tarjeta pintada, el fallo ocupa su sitio con «Volver a cargar». Con ella pintada, lo leído
     * sigue a la vista con su hora y el aviso al lado, y si hay solicitud viva el sondeo no se corta:
     * antes un solo fallo lo paraba y la tarjeta se quedaba con «Pendiente» y los botones apagados.
     */
    fallarLectura: function(motivo) {
      this.cargando = false;
      if (!this.ficha) {
        this.errorCarga = motivo;
        return;
      }
      this.errorSondeo = motivo + (this.vivaSolicitud ? ' Lo de arriba es lo último que se leyó; se vuelve a intentar sola.' : '');
      this.pararRecarga();
      if (this.vivaSolicitud && this.activa) {
        this.temporizador = setTimeout(() => this.cargar(), RECARGA_MS);
      }
    },
    /**
     * Pone `ocupado` mientras espera una petición y lo suelta pase lo que pase, también si no contesta
     * nunca: entonces avisa y llama a `alVencer`. Lo que llegue después no vuelve a soltar un `ocupado`
     * que ya puede ser de otra acción. Soltarlo sin saber si la petición se aplicó no duplica nada: el
     * servidor solo encola, vincula o relanza si la solicitud está en el estado que lo permite.
     */
    ocuparHasta: function(alVencer) {
      cerrojoDeLaSesion(this, 'ocupado');
      this.errorAccion = null;
      const pedido = this.empleadoId;
      let vencida = false;
      const vigia = setTimeout(() => {
        vencida = true;
        if (!this.activa) return;
        this.ocupado = false;
        if (pedido === this.empleadoId) {
          this.errorAccion = SIN_RESPUESTA + ' Puede que se haya aplicado: comprueba el estado en la tarjeta antes de repetirlo.';
        }
        if (alVencer) alVencer();
      }, ACCION_MAX_MS);
      return {
        vigente: () => !vencida,
        soltar: () => {
          clearTimeout(vigia);
          if (!vencida) this.ocupado = false;
        }
      };
    },
    /** Pinta la tarjeta. El formulario solo se rellena al cargar por primera vez o tras guardar. */
    pintar: function(r, rellenarFormulario) {
      if (!this.activa) return;
      this.ficha = r;
      if (rellenarFormulario) {
        this.form = {
          perfil: r.perfil || r.perfilSugerido || null,
          correo: r.correo || r.correoSugerido || '',
          nombre: r.nombre || r.nombreSugerido || '',
          apellidos: r.apellidos || r.apellidosSugeridos || '',
          ubicacionUso: r.ubicacionUso || r.ubicacionPorDefecto || 'ES'
        };
      }
      this.pararRecarga();
      if (this.vivaSolicitud) {
        this.temporizador = setTimeout(() => this.cargar(), RECARGA_MS);
      }
    },
    /**
     * Lo que todavía no se ha guardado en Microsoft 365 es una sugerencia sacada de la ficha: si la
     * ficha cambia, cambia con ella. Lo que ya está guardado, o lo que se ha escrito a mano en la
     * tarjeta, no se toca.
     */
    refrescarSugerencias: function(anterior, r) {
      SUGERENCIAS.forEach(([campo, sugerido, vacio]) => {
        if (r[campo]) return;
        const pintado = anterior[campo] || anterior[sugerido] || vacio;
        if ((this.form[campo] || vacio) === pintado) {
          this.form[campo] = r[sugerido] || vacio;
        }
      });
    },
    trabajar: function(promesa, rellenar) {
      let vencer;
      const vencimiento = new Promise(ok => { vencer = ok; });
      // Sin respuesta a tiempo, la acción deja de estar en vuelo y se relee la ficha para ver qué hizo;
      // quien espera a `trabajar` (el guardado suelta `guardando`) recibe null, como en un error.
      const ocupado = this.ocuparHasta(() => {
        this.enAccion = false;
        const pendiente = this.lecturaPendiente;
        this.lecturaPendiente = null;
        this.cargar(pendiente === true);
        vencer(null);
      });
      // Atado a la sesión como `ocupado`: si se quedara puesto, la tarjeta no volvería a leer nunca.
      cerrojoDeLaSesion(this, 'enAccion');
      // Una recarga en camino, o a punto de salir, traería la ficha de antes de la acción y la pintaría
      // encima de su resultado: se para y se invalida, y la propia respuesta vuelve a programarla.
      this.pararRecarga();
      const turno = ++this.turno;
      this.cargando = false;
      const pedido = this.empleadoId;
      const accion = promesa.then(r => {
        if (ocupado.vigente() && r && r.empleadoId && turno === this.turno) {
          this.pintar(r, rellenar);
        }
        return r;
      }).catch(e => {
        // Vencida, `ocuparHasta` ya la ha dado por perdida y ha releído la ficha: el error tardío no se cuenta.
        if (ocupado.vigente()) {
          // El error de una acción sobre otra persona no se cuenta en la tarjeta de esta.
          if (pedido === this.empleadoId) this.errorAccion = (e && e.message) || 'No se ha podido completar la acción.';
          // Sin respuesta que pintar, se relee la ficha: así sigue al día y, si hay solicitud viva, se sigue sondeando.
          if (turno === this.turno) this.cargar();
        }
        return null;
      }).then(r => {
        if (!ocupado.vigente()) return null;
        ocupado.soltar();
        this.enAccion = false;
        const pendiente = this.lecturaPendiente;
        this.lecturaPendiente = null;
        if (pendiente !== null) this.cargar(pendiente);
        return r;
      });
      return Promise.race([accion, vencimiento]);
    },
    guardar: function() {
      cerrojoDeLaSesion(this, 'guardando');
      this.trabajar(Microsoft365.guardarDatos(this.empleadoId, this.form, ACCION_MAX_MS), true).then(r => {
        this.guardando = false;
        if (r) {
          this.olvidarConsultas();
          this.$f7.toast.create({ text: 'Datos de Microsoft 365 guardados', position: 'center', closeTimeout: 2000 }).open();
        }
      });
    },
    confirmar: function(texto, titulo, alAceptar) {
      this.$f7.dialog.confirm(texto, titulo, alAceptar);
    },
    solicitarAlta: function() {
      const simulado = this.estado.modo === 'SIMULADO' ? ' (en el tenant SIMULADO)' : '';
      const perfil = PERFILES.find(p => p.id === this.ficha.perfil) || {};
      this.confirmar('Se va a crear en Microsoft 365' + simulado + ' la cuenta ' + escapar(this.ficha.correo)
        + ' con perfil ' + escapar(perfil.nombre) + '. El resultado se verá paso a paso en esta tarjeta.', 'Solicitar alta', () => {
        this.trabajar(Microsoft365.solicitarAlta(this.empleadoId, ACCION_MAX_MS));
      });
    },
    comprobar: function() {
      this.olvidarConsultas();
      const sello = this.sellar(true);
      const peticion = Microsoft365.correspondencia(sello.empleadoId, ACCION_MAX_MS);
      // La cuenta que llegue tarde ya no se ofrece para vincular: se habría buscado hace demasiado.
      const ocupado = this.ocuparHasta(() => { this.consulta++; });
      peticion.then(r => {
        if (this.vigente(sello)) this.correspondencia = { conflictos: [], ...r };
      }).catch(e => {
        if (this.vigente(sello)) this.errorAccion = (e && e.message) || 'No se ha podido consultar Microsoft.';
      }).then(ocupado.soltar);
    },
    vincular: function() {
      const c = this.correspondencia;
      this.confirmar('Se va a vincular esta ficha con ' + escapar(c.upn) + '. No se recrea la cuenta ni se cambia su'
        + ' contraseña ni su licencia; se añadirá a los grupos de su perfil y a SIMECAL EQUIPO si le faltan.',
      'Vincular cuenta existente', () => {
        // Si mientras se decidía se ha descartado esa cuenta (otra persona, o se ha guardado otro correo), no se vincula.
        if (this.correspondencia !== c) return;
        this.trabajar(Microsoft365.vincular(this.empleadoId, c.entraObjectId, ACCION_MAX_MS)).then(r => {
          if (r) this.correspondencia = null;
        });
      });
    },
    reintentar: function() {
      this.trabajar(Microsoft365.reintentar(this.empleadoId, ACCION_MAX_MS));
    },
    confirmarArchivo: function() {
      this.confirmar('¿Declaras que un administrador de Exchange ha aplicado a este buzón el archivo en línea, el traslado'
        + ' a los 730 días y la conservación de 3650 días con los pasos de la guía? Quedará registrado a tu nombre.'
        + ' Esto <b>no es una verificación técnica</b>: el paso quedará «sin verificar» y el alta no se dará por completada.',
      'Archivo y conservación', () => {
        this.trabajar(Microsoft365.confirmarArchivo(this.empleadoId, ACCION_MAX_MS));
      });
    },
    accesoTemporal: function() {
      const upn = this.ficha.vinculo.upn;
      this.confirmar('Se va a emitir un acceso temporal de un solo uso para ' + escapar(upn)
        + '. Lo verás una sola vez: no se guarda en ningún sitio.', 'Acceso temporal', () => {
        const pedido = this.empleadoId;
        const peticion = Microsoft365.accesoTemporal(pedido, ACCION_MAX_MS);
        const ocupado = this.ocuparHasta();
        peticion.then(r => {
          // El código va directo al diálogo y no se guarda en el componente: cerrado el diálogo, no queda.
          // Ya está emitido, así que se enseña aunque entretanto se haya cambiado de persona; por eso
          // dice de qué cuenta es, y no se da por hecho que es la de la tarjeta que haya a la vista.
          this.$f7.dialog.alert('<div>Cuenta: <b>' + escapar(upn) + '</b></div>'
            + '<div style="font-size:20px;font-weight:bold;letter-spacing:1px">' + escapar(r.codigo)
            + '</div><div>Caduca: ' + escapar(this.fechaHora(r.caducaEn)) + '</div><p>' + escapar(r.aviso) + '</p>',
          'Acceso temporal');
        }).catch(e => {
          if (pedido === this.empleadoId && ocupado.vigente()) {
            this.errorAccion = (e && e.message) || 'No se ha podido emitir el acceso temporal.';
          }
        }).then(ocupado.soltar);
      });
    },
    textoEquipos: function(r) {
      if (!r || !r.consultado) {
        return 'No se puede consultar: la aplicación no tiene el permiso de lectura de Intune (ver la guía).';
      }
      return r.equipos === 1 ? 'Tiene 1 equipo inscrito en Intune.' : 'Tiene ' + r.equipos + ' equipos inscritos en Intune.';
    },
    consultarEquipos: function() {
      const sello = this.sellar(false);
      const peticion = Microsoft365.equipos(sello.empleadoId, ACCION_MAX_MS);
      const ocupado = this.ocuparHasta();
      peticion.then(r => {
        if (this.vigente(sello)) this.equipos = this.textoEquipos(r);
      }).catch(e => {
        if (this.vigente(sello) && ocupado.vigente()) this.errorAccion = (e && e.message) || 'No se ha podido consultar Intune.';
      }).then(ocupado.soltar);
    }
  }
};
</script>

<style scoped>
.tarjeta-m365 .card-header { font-weight: bold; color: #22355b; }
.m365-modo { padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; color: #fff; background: #5f6368; }
.modo-SIMULADO { background: #a34800; }
.modo-REAL { background: #22355b; }
.m365 { margin: 0 16px 8px; font-size: 13px; }
.m365 .nota { color: #666; font-size: 12px; }
.m365 .aviso { color: #b45309; font-weight: bold; }
.bloque { border-top: 1px solid #eef0f4; padding: 8px 0; }
.bloque.dos { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
.titulo { font-weight: 600; color: #22355b; margin-bottom: 4px; }
.rejilla { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 8px 12px; }
.rejilla label { display: flex; flex-direction: column; font-size: 12px; color: #555; gap: 2px; }
.rejilla input, .rejilla select { border: 1px solid #ccd; border-radius: 4px; padding: 4px 6px; background: #fff; font-size: 13px; }
.rejilla input:disabled, .rejilla select:disabled { background: #f4f5f8; color: #666; }
.acciones { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 6px; }
.accion { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; max-width: 420px; margin-top: 6px; }
.motivo { color: #b45309; font-size: 12px; }
.m365btn { border: 1px solid #23355b; background: #23355b; color: #fff; border-radius: 4px; padding: 4px 12px; cursor: pointer; }
.m365btn.sec { background: #fff; color: #23355b; }
.m365btn:disabled { opacity: .45; cursor: not-allowed; }
.avisos { margin: 6px 0; padding: 8px 16px 8px 28px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; color: #6b4e00; }
.fallo { border: 1px solid #f3c2c2; background: #fff5f5; border-radius: 6px; padding: 8px 12px; margin: 8px 0; color: #a61b1b; }
.estado-carga { display: flex; align-items: center; gap: 8px; color: #555; padding: 8px 0; }
.correspondencia { margin-top: 8px; padding: 8px 12px; border: 1px solid #e3e6ee; border-radius: 6px; background: #fafbfc; }
.pasos { width: 100%; border-collapse: collapse; margin-top: 6px; }
.pasos th { text-align: left; font-size: 12px; font-weight: 600; color: #555; border-bottom: 1px solid #e3e6ee; padding: 4px 8px; }
.pasos td { border-bottom: 1px solid #f0f1f5; padding: 6px 8px; vertical-align: top; }
.pasos .nombre { font-weight: 600; white-space: nowrap; }
.que-hacer { color: #b45309; margin-top: 2px; }
.pertenencias { margin: 4px 0 0; padding-left: 0; list-style: none; }
.pertenencias li + li { margin-top: 2px; }
.chip { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: bold; color: #fff; background: #5f6368; white-space: nowrap; }
/* Colores con contraste suficiente para texto blanco (4,5:1 o más). */
.e-EN_CURSO { background: #0b5cad; }
.e-COMPLETADO, .e-COMPLETADA, .e-YA_CONFIGURADO { background: #1e7b34; }
.e-REQUIERE_INTERVENCION, .e-SIN_VERIFICAR { background: #a34800; }
.e-ERROR { background: #b3261e; }
</style>
